/* eslint-disable max-lines */
import {
  AttributeFormatDc,
  ColumnDescriptionDc,
  DetailedTableInfoDc,
  FeatureDc,
  FeatureLayerServiceInfoDc,
  GetLayerExtentParams,
  GetLayersListParams,
  LayerUpdateInfoDc,
  PolygonDc,
  PolylineDc,
  PostgresLayerServiceConfigurationDc,
  PostgresTileCatalogServiceConfigurationDc,
  ProxyServiceConfigurationDc,
  RemoteTileServiceConfigurationDc,
  ResourceInfoDc,
  ServiceInfoDc,
  StringSubType,
  StyleDc,
  TableInfoDc,
  UpdateFeatureDc,
  ValidateExpressionParams,
  AccessControlListDc,
  QueryLayerServiceConfigurationDc,
  LocalTileServiceConfigurationDc,
  AttributeType,
  AttributeConfigurationDc,
  GeometryType,
} from "@evergis/api";

import { RangeValue } from "@evergis/ui";

import { createMutation, createResource } from "@evergis/react-fetching";
import { createNewAttribute } from "../evergis/core/attributes/createNewAttribute";

import { isPolygonFeature, isPolylineFeature } from "../interfaces/features";
import { promiseAllSettled } from "../utils/promiseAllSettled";
import { getResourceName } from "../utils/resourceName";
import { features } from "./features";
import { layerWithTable } from "./layerWithTable";
import { projectInfosWithDeps } from "./projects";
import { getTableRefs, getTablesRefs, isLayerRef, tables } from "./tables";

import { api as evergisApi } from "../evergis/api";
import { SystemAttributeOption } from "../components/Dialogs/DialogComponents/Attributes/types";
import { Attribute } from "../interfaces/service";
import { ResourcesFetchParams } from "../modules/Catalog/hooks/useFetchParams";
import { LayerAttribute } from "../components/Dialogs/EditResource/components/Layer/LayerAttributeItem";
import { RemoteTileServiceConfiguration } from "../modules/Configuration/types";

type LayerInputColumn = PickAndOmit<ColumnDescriptionDc, Partial<Attribute>, "type"> & {
  subType?: StringSubType;
  stringFormat?: AttributeFormatDc;
};

export type CreateLayerInput = {
  idAttribute: string;
  geometryAttribute: string;
  titleAttribute?: string;
  attributes?: LayerAttribute[];
  dataTable: Override<DetailedTableInfoDc, "columns", LayerInputColumn[]>;
  style?: StyleDc;
  condition?: string;
  layerName?: string;
  dataServiceName?: string;
  description?: string;
  copyrightText?: string;
  sysAttributes?: boolean;
  isCogLayer?: boolean;
  acl?: AccessControlListDc;
  srid?: number;
  geometryType?: GeometryType;
};

export type RefsWithLimitInput = {
  name: string;
  extendedRefsLimit: number;
};

export type ExtendedLayerRefInfo = ServiceInfoDc | ResourceInfoDc;

export type LayersRefs = {
  my: (ExtendedLayerRefInfo | string)[];
  others: string[];
};

export type LayerWithTableRefs = {
  layer: ServiceInfoDc;
  table: TableInfoDc | null;
  layerRefs: LayersRefs | null;
};

export const SYS_ATTRIBUTES_NAMES = [
  SystemAttributeOption.DateOfEdit,
  SystemAttributeOption.DateOfCreate,
  SystemAttributeOption.Author,
  SystemAttributeOption.LastEditor,
];

export const layers = createResource(
  ({ category, ...params }: ResourcesFetchParams) =>
    async api => {
      return {
        data: await api.layers.findAll(params as unknown as GetLayersListParams),
      };
    },
  { injected: evergisApi },
);

export const layer = createResource(
  (name: string) => async api => ({
    data: await api.layers.findOne(name),
  }),
  { injected: evergisApi },
);

function addDefaultsToIdAttribute(dataTable: DetailedTableInfoDc, idAttribute: string) {
  const newColumns = dataTable.columns.map(column => {
    if (column.name === idAttribute) {
      return { ...column, isNullable: false, isUnique: true, autoincrement: true };
    }

    return column;
  });

  return { ...dataTable, columns: newColumns };
}

const columnsToAttributes = (columns: LayerInputColumn[], idAttribute?: string) =>
  columns.map(({ name: columnName, alias: columnAlias, subType, stringFormat }) => {
    const attribute = { attributeName: columnName, columnName, alias: columnAlias, subType, stringFormat };

    if (columnName === idAttribute) {
      return { ...attribute, isNullable: false, isUnique: true, autoincrement: true, isDisplayed: false };
    }

    return attribute;
  });

const createLayerConfiguration = (
  {
    attributes,
    dataTable,
    idAttribute,
    geometryAttribute,
    titleAttribute,
    style,
    condition,
    layerName,
    acl,
    copyrightText,
    isCogLayer,
  }: CreateLayerInput,
  isCopy = false,
): PostgresLayerServiceConfigurationDc | PostgresTileCatalogServiceConfigurationDc => {
  const { name, alias, description, columns } = dataTable;

  const calculatedAttributes = attributes?.filter(attribute => attribute.expression) || [];
  const attributesSubType = attributes?.filter(attribute => attribute.subType) || [];
  const attributesStringFormat = attributes?.filter(attribute => attribute.stringFormat) || [];

  const columnsAttributes = columns.map(column => {
    const columnSubType = attributesSubType?.find(item => item.attributeName === column.name);
    const columnStringFormat = attributesStringFormat?.find(item => item.attributeName === column.name);

    return {
      subType: columnSubType && columnSubType.subType,
      stringFormat: columnStringFormat && columnStringFormat.stringFormat,
      ...column,
    };
  });

  return {
    alias,
    name: layerName || getResourceName(),
    description,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    copyrightText,
    style,
    condition,
    attributesConfiguration: {
      idAttribute,
      geometryAttribute,
      titleAttribute,
      tableName: name,
      attributes: isCopy
        ? attributes
        : [...columnsToAttributes(columnsAttributes, idAttribute), ...calculatedAttributes],
    },
    acl,
    isCogLayer,
  };
};

const mergeColumns = (columns: ColumnDescriptionDc[], inputColumns: LayerInputColumn[]): LayerInputColumn[] =>
  columns.map(column => ({
    ...(inputColumns.find(({ name }) => column.name === name) || {}),
    ...column,
  }));

export const layerInfosWithDeps = createResource(
  (names: string[]) => async api => ({
    data: await api.resources.getLayerInfosWithDeps(names),
  }),
  { injected: evergisApi },
);

export const layerInfos = createResource(
  (names: string[]) => async api => {
    return {
      data: await promiseAllSettled(names.map(name => api.layers.getLayerInfo(name))),
    };
  },
  { injected: evergisApi },
);

export const layerWithTableRefs = createResource(
  ({ name, extendedRefsLimit }: RefsWithLimitInput) =>
    async api => {
      const {
        layers: [layerInfo],
        deps: { tables: layerTables },
      } = await api.resources.getLayerInfosWithDeps([name]);

      if (Array.isArray(layerTables) && layerTables.length > 0) {
        const [table] = layerTables;
        const layerRefs = await getTableRefs(table.name, isLayerRef);

        const { username } = api.account;
        const myLayersRefs = layerRefs.filter(layerName => layerName !== name && layerName.startsWith(username));
        const otherLayersRefs = layerRefs.filter(layerName => !layerName.startsWith(username));

        return {
          data: {
            layer: layerInfo,
            table,
            layerRefs: {
              my: [
                ...(await promiseAllSettled(
                  myLayersRefs.slice(0, extendedRefsLimit).map(layerName => api.layers.getLayerInfo(layerName)),
                )),
                ...myLayersRefs.slice(extendedRefsLimit, myLayersRefs.length),
              ],
              others: otherLayersRefs,
            },
          },
        };
      }

      return {
        data: {
          layer: layerInfo,
          table: null,
          layerRefs: null,
        },
      };
    },
  { injected: evergisApi },
);

export type LayerRefsWithTablesInput = {
  names: string[];
  extendedRefsLimit: number;
};

export const layerRefsWithTables = createResource(
  ({ names, extendedRefsLimit }: LayerRefsWithTablesInput) =>
    async api => {
      const layerRefs = await getTablesRefs(names, isLayerRef);

      const { username } = api.account;
      const myLayersRefs = layerRefs.filter(layerName => layerName.startsWith(username));
      const otherLayersRefs = layerRefs.filter(layerName => !layerName.startsWith(username));

      return {
        data: {
          my: [
            ...(await promiseAllSettled(
              myLayersRefs.slice(0, extendedRefsLimit).map(layerName => api.layers.getLayerInfo(layerName)),
            )),
            ...myLayersRefs.slice(extendedRefsLimit, myLayersRefs.length),
          ],
          others: otherLayersRefs,
        },
      };
    },
  { injected: evergisApi },
);

export const createLayer = createMutation(
  (layerInput: CreateLayerInput) => async api => ({
    data: await api.layers.create(createLayerConfiguration(layerInput)),
  }),
  {
    injected: evergisApi,
    update: () => {
      layers.invalidateAll();
      layerInfos.invalidateAll();
      projectInfosWithDeps.invalidateAll();
    },
  },
);

export const createPostgresLayer = createMutation(
  (layerInput: PostgresLayerServiceConfigurationDc) => async api => ({
    data: await api.layers.create(layerInput),
  }),
  {
    injected: evergisApi,
    update: () => {
      layers.invalidateAll();
      layerInfos.invalidateAll();
      projectInfosWithDeps.invalidateAll();
    },
  },
);

export const createQueryLayer = createMutation(
  (layerInput: QueryLayerServiceConfigurationDc) => async api => ({
    data: await api.layers.createQueryLayer(layerInput),
  }),
  {
    injected: evergisApi,
    update: () => {
      layers.invalidateAll();
      layerInfos.invalidateAll();
      projectInfosWithDeps.invalidateAll();
    },
  },
);

export const createTileLayer = createMutation(
  (layerInput: PostgresTileCatalogServiceConfigurationDc) => async api => ({
    data: await api.layers.publishPostgresTileCatalogService(layerInput),
  }),
  {
    injected: evergisApi,
    update: () => {
      layers.invalidateAll();
      layerInfos.invalidateAll();
      projectInfosWithDeps.invalidateAll();
    },
  },
);

export const createTileLayerWithTable = createResource(
  ({
      idAttribute,
      geometryAttribute,
      dataTable,
      style = null,
      attributes,
      layerName,
      acl,
      description,
      copyrightText,
      isCogLayer,
    }: CreateLayerInput) =>
    async api => {
      const { columns, name: tableName } = (await api.tables.create({
        ...addDefaultsToIdAttribute(dataTable as DetailedTableInfoDc, idAttribute),
        configuration: { type: "tileCatalogTableConfiguration" },
      })) as DetailedTableInfoDc;

      try {
        const createdLayer = await api.layers.publishPostgresTileCatalogService(
          createLayerConfiguration({
            dataServiceName: "data",
            description,
            attributes,
            layerName,
            dataTable: {
              ...dataTable,
              columns: mergeColumns(columns, dataTable.columns).filter(
                item => !SYS_ATTRIBUTES_NAMES.includes(item.name as SystemAttributeOption),
              ),
            },
            geometryAttribute,
            style,
            idAttribute,
            acl,
            copyrightText,
            isCogLayer,
          }),
        );

        return {
          data: createdLayer,
        };
      } catch (e) {
        api.tables.remove(tableName);
      }

      return {
        data: void 0,
      };
    },
  {
    injected: evergisApi,
  },
);

export const createLayerWithTable = createMutation(
  ({
      idAttribute,
      geometryAttribute,
      dataTable,
      style = null,
      attributes,
      layerName,
      acl,
      copyrightText,
    }: CreateLayerInput) =>
    async api => {
      const createPayload = {
        ...addDefaultsToIdAttribute(dataTable as DetailedTableInfoDc, idAttribute),
        acl,
      };
      const { columns, name: tableName } = (await api.tables.create(createPayload)) as DetailedTableInfoDc;
      let createdLayer: ServiceInfoDc | ResourceInfoDc | FeatureLayerServiceInfoDc;

      try {
        createdLayer = await api.layers.create(
          createLayerConfiguration({
            attributes,
            layerName,
            dataTable: {
              ...dataTable,
              name: tableName,
              columns: mergeColumns(columns, dataTable.columns).filter(
                item => !SYS_ATTRIBUTES_NAMES.includes(item.name as SystemAttributeOption),
              ),
            },
            geometryAttribute,
            style,
            idAttribute,
            acl,
            copyrightText,
          }),
        );

        return {
          data: createdLayer,
        };
      } finally {
        if (!createdLayer) {
          api.tables.remove(tableName);
        }
      }

      return {
        data: void 0,
      };
    },
  {
    injected: evergisApi,
    update: () => {
      layers.invalidateAll();
      tables.invalidateAll();
      layerInfos.invalidateAll();
      projectInfosWithDeps.invalidateAll();
    },
  },
);

export const batchCreateLayersWithTables = createMutation(
  (input: CreateLayerInput[]) => async api => {
    const createSingleLayerWithTable = async ({
      idAttribute,
      geometryAttribute,
      dataTable,
      style = null,
      attributes,
      layerName,
      acl,
      copyrightText,
    }: CreateLayerInput) => {
      const createPayload = {
        ...addDefaultsToIdAttribute(dataTable as DetailedTableInfoDc, idAttribute),
        acl,
      };
      const { columns, name: tableName } = (await api.tables.create(createPayload)) as DetailedTableInfoDc;

      try {
        const createdLayer = await api.layers.create(
          createLayerConfiguration({
            attributes,
            layerName,
            dataTable: {
              ...dataTable,
              columns: mergeColumns(columns, dataTable.columns).filter(
                item => !SYS_ATTRIBUTES_NAMES.includes(item.name as SystemAttributeOption),
              ),
            },
            geometryAttribute,
            style,
            idAttribute,
            acl,
            copyrightText,
          }),
        );

        return createdLayer.name;
      } catch (e) {
        api.tables.remove(tableName);
      }
    };

    return {
      data: await Promise.all(input.map(inputItem => createSingleLayerWithTable(inputItem))),
    };
  },
  {
    injected: evergisApi,
    update: () => {
      layers.invalidateAll();
      tables.invalidateAll();
      layerInfos.invalidateAll();
      projectInfosWithDeps.invalidateAll();
    },
  },
);

export const batchCreateQueryLayersWithTables = createMutation(
  // eslint-disable-next-line max-lines-per-function
  (input: CreateLayerInput[]) => async api => {
    const createSingleLayerWithTable = async ({
      idAttribute,
      geometryAttribute,
      dataTable,
      style = null,
      attributes,
      layerName,
      acl,
      copyrightText,
      srid,
      geometryType,
    }: CreateLayerInput) => {
      const createPayload = {
        ...addDefaultsToIdAttribute(dataTable as DetailedTableInfoDc, idAttribute),
        acl,
      };
      const { name: tableName } = (await api.tables.create(createPayload)) as DetailedTableInfoDc;

      try {
        const createdLayer = await api.layers.createQueryLayer({
          name: layerName,
          alias: createPayload.alias,
          description: createPayload.description,
          geometryType: geometryType
            ? ((geometryType.charAt(0).toUpperCase() + geometryType.slice(1)) as unknown as AttributeType)
            : AttributeType.Unknown,
          eql: `SELECT * FROM ${tableName}`,
          copyrightText,
          srid,
          style,
          attributesConfiguration: {
            attributes: attributes.map(
              ({
                type,
                expression,
                stringFormat,
                attributeName,
                alias: attributeAlias,
                sysAttribute,
                columnName,
                ...otherData
              }) =>
                createNewAttribute({
                  type,
                  attributeName,
                  alias: attributeAlias,
                  expression,
                  stringFormat,
                  columnName: sysAttribute ? columnName : null,
                  ...otherData,
                }),
            ) as AttributeConfigurationDc[],
            tableName,
            idAttribute,
            geometryAttribute,
          },
        });

        return createdLayer;
      } catch (e) {
        api.tables.remove(tableName);
      }
    };

    return {
      data: await Promise.all(input.map(inputItem => createSingleLayerWithTable(inputItem))),
    };
  },
  {
    injected: evergisApi,
    update: () => {
      layers.invalidateAll();
      tables.invalidateAll();
      layerInfos.invalidateAll();
      projectInfosWithDeps.invalidateAll();
    },
  },
);

export const updateLayer = createMutation(
  (input: { configuration: PostgresLayerServiceConfigurationDc; defaultScaleRange: RangeValue }) => async api => {
    const { configuration, defaultScaleRange } = input;
    let errors = [];
    let result;

    try {
      result = await api.layers.update(configuration);
    } catch (error) {
      const json = await error.response.json();

      errors = Object.keys(json.errors)
        .reverse()
        .reduce(
          (prevErrors, key) => [
            ...prevErrors,
            ...json.errors[key].map(errorString => (key ? [key, errorString].join(": ") : errorString)),
          ],
          [],
        );
    }

    return {
      data: { name: result?.name, defaultScaleRange },
      meta: { errors, status: errors ? "layerError" : "" },
    };
  },
  {
    injected: evergisApi,
    update: ({ name, defaultScaleRange }) => {
      layer.invalidate(name);
      layerWithTable.invalidate({ name, defaultScaleRange });
      layers.invalidateAll();
      layerInfos.invalidateAll();
      projectInfosWithDeps.invalidateAll();
      features.invalidateAll();
    },
  },
);

export const updateTileLayer = createMutation(
  (input: { configuration: PostgresTileCatalogServiceConfigurationDc; defaultScaleRange: RangeValue }) => async api => {
    const { configuration, defaultScaleRange } = input;
    const result = await api.layers.updateTileCatalogService(configuration.name, configuration);

    return {
      data: { name: result.name, defaultScaleRange },
    };
  },
  {
    injected: evergisApi,
    update: ({ name, defaultScaleRange }) => {
      layer.invalidate(name);
      layerWithTable.invalidate({ name, defaultScaleRange });
      layers.invalidateAll();
      layerInfos.invalidateAll();
      projectInfosWithDeps.invalidateAll();
      features.invalidateAll();
    },
  },
);

export const updateQueryLayer = createMutation(
  (input: { configuration: QueryLayerServiceConfigurationDc; defaultScaleRange: RangeValue }) => async api => {
    const { configuration, defaultScaleRange } = input;
    const result = await api.layers.updateQueryLayerService(configuration.name, configuration);

    return {
      data: { name: result.name, defaultScaleRange, layer: result },
    };
  },
  {
    injected: evergisApi,
    update: ({ name, defaultScaleRange }) => {
      layer.invalidate(name);
      layerWithTable.invalidate({ name, defaultScaleRange });
      layers.invalidateAll();
      layerInfos.invalidateAll();
      projectInfosWithDeps.invalidateAll();
      features.invalidateAll();
    },
  },
);

export const updateRemoteTileLayer = createMutation(
  (input: RemoteTileServiceConfiguration) => async api => {
    const { resolution, defaultScaleRange, ...data } = input;
    const result = await api.layers.updateRemoteTileService(data.name, data);

    return {
      data: {
        name: data.name,
        defaultScaleRange,
        resolution,
        ...result,
      },
    };
  },
  {
    injected: evergisApi,
    update: ({ name, defaultScaleRange }) => {
      layer.invalidate(name);
      layerWithTable.invalidate({ name, defaultScaleRange });
      layers.invalidateAll();
      layerInfos.invalidateAll();
      projectInfosWithDeps.invalidateAll();
      features.invalidateAll();
    },
  },
);

export const updateFeature = createMutation(
  ({ layerName, feature }: { layerName: string; feature: UpdateFeatureDc }) =>
    async api => {
      let result: LayerUpdateInfoDc;

      try {
        result = await api.layers.updateFeature(layerName, [feature]);
      } catch (e) {
        // TODO send notification
      }

      return {
        data: result,
      };
    },
  { injected: evergisApi },
);

export const ungroupFeature = createMutation(
  ({ layerName, feature }: { layerName: string; feature: FeatureDc }) =>
    async api => {
      const geometricFeatures = [];

      if (isPolylineFeature(feature) || isPolygonFeature(feature)) {
        (feature.geometry as PolylineDc | PolygonDc).coordinates.forEach(coordinates => {
          geometricFeatures.push({
            attributes: { ...feature.attributes },
            geometry: {
              ...feature.geometry,
              coordinates: [coordinates],
            },
          });
        });
      }

      if (geometricFeatures.length) {
        const [featureToUpdate, ...featuresToCreate] = geometricFeatures;

        if (featureToUpdate) {
          if (feature.id) {
            await api.layers.updateFeature(layerName, [
              {
                ...featureToUpdate,
                id: feature.id,
              },
            ]);
          }
        }

        if (featuresToCreate.length || !feature.id) {
          await api.layers.createFeatures(layerName, !feature.id ? geometricFeatures : featuresToCreate);
        }
      }

      return {
        data: geometricFeatures,
      };
    },
  {
    injected: evergisApi,
  },
);

export const layerExtent = createMutation(
  ({ name: layerName, sr, condition }: GetLayerExtentParams) =>
    async api => {
      return {
        data: await api.layers.getLayerExtent({ name: layerName, sr, condition }),
      };
    },
  { injected: evergisApi },
);

export const filteredFeaturesCount = createResource(
  (name: string) => async api => {
    return {
      data: await api.layers.getFilteredFeaturesCount({ name }),
    };
  },
  { injected: evergisApi },
);

export const validateExpression = createMutation(
  ({ layerName, expression }: ValidateExpressionParams) =>
    async api => {
      return {
        data: await api.layers.validateExpression({ layerName, expression }),
      };
    },
  { injected: evergisApi },
);

export const createProxyLayer = createMutation(
  (data: ProxyServiceConfigurationDc) => async api => {
    return {
      data: await api.layers.publishProxyService(data),
    };
  },
  {
    injected: evergisApi,
    update: () => {
      layers.invalidateAll();
    },
  },
);

export const createRemoteTileLayer = createMutation(
  (data: RemoteTileServiceConfigurationDc) => async api => {
    return {
      data: await api.layers.publishRemoteTileService(data),
    };
  },
  {
    injected: evergisApi,
    update: () => {
      layers.invalidateAll();
    },
  },
);

export const createLocalTileLayer = createMutation(
  (data: LocalTileServiceConfigurationDc) => async api => {
    return {
      data: await api.layers.publishLocalTileService(data),
    };
  },
  {
    injected: evergisApi,
    update: () => {
      layers.invalidateAll();
    },
  },
);

export const updateProxyService = createMutation(
  ({
      name,
      data,
      resolution,
      defaultScaleRange,
    }: {
      name: string;
      data: ProxyServiceConfigurationDc;
      resolution?: number;
      defaultScaleRange?: RangeValue;
    }) =>
    async api => {
      const result = await api.layers.updateProxyService(name, data);

      return {
        data: {
          name,
          resolution,
          defaultScaleRange,
          ...result,
        },
      };
    },
  {
    injected: evergisApi,
    update: ({ name, defaultScaleRange }) => {
      layer.invalidate(name);
      layerWithTable.invalidate({ name, defaultScaleRange });
      layers.invalidateAll();
      layerInfos.invalidateAll();
      projectInfosWithDeps.invalidateAll();
      features.invalidateAll();
    },
  },
);

export const updatePostgresLayerService = createMutation(
  ({
      name,
      data,
      resolution,
      defaultScaleRange,
    }: {
      name: string;
      data: PostgresLayerServiceConfigurationDc;
      resolution?: number;
      defaultScaleRange?: RangeValue;
    }) =>
    async api => {
      const result = await api.layers.updatePostgresLayerService(name, data);

      return {
        data: {
          name,
          resolution,
          defaultScaleRange,
          ...result,
        },
      };
    },
  {
    injected: evergisApi,
    update: ({ name, defaultScaleRange }) => {
      layer.invalidate(name);
      layerWithTable.invalidate({ name, defaultScaleRange });
      layers.invalidateAll();
      layerInfos.invalidateAll();
      projectInfosWithDeps.invalidateAll();
      features.invalidateAll();
    },
  },
);

export const layerDependencies = createResource(
  (name: string) => async api => {
    return {
      data: await api.layers.getResourceDependencies(name),
    };
  },
  { injected: evergisApi },
);
